import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Terminal } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent } from 'react'

import { Alert, Button, Card, CardHeader, ConfirmDialog, SelectField, SkeletonRows, TextField, mutedTextClass } from '@/components/layout/ui'
import { aiCredentialsApi, ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { useCopyToClipboard } from '@/lib/hooks'
import { messageFor } from '@/lib/errors'
import { formatDateTime } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { useSession } from '@/lib/session'
import { issueKeys } from '@/features/issue/keys'
import type { AiAuthorizeResponse, AiCredentialStatus, AiProvider } from '@/lib/types'
import { useWorkspace } from '@/lib/workspace'

const providerName = (provider?: string) => provider === 'openai' ? 'ChatGPT' : 'Claude'

export default function AIPage() {
  const workspace = useWorkspace()
  return <AISettings key={workspace.slug} />
}

function AISettings() {
  const workspace = useWorkspace()
  const queryClient = useQueryClient()
  const { refresh } = useSession()
  const canManage = workspace.can('org.update')
  const status = useQuery({
    queryKey: queryKeys.aiCredential(workspace.slug),
    queryFn: ({ signal }) => aiCredentialsApi.status(workspace.slug, { signal }),
    enabled: canManage,
  })
  const [choice, setChoice] = useState<AiProvider | null>(null)
  const provider = choice ?? (status.data?.provider === 'openai' ? 'openai' : 'anthropic')
  const name = providerName(provider)
  const [authorization, setAuthorization] = useState<AiAuthorizeResponse | null>(null)
  const [code, setCode] = useState('')
  const [deviceError, setDeviceError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const connected = canManage ? status.data?.connected === true : workspace.membership.ai_connected === true
  const selectedConnected = connected && status.data?.provider === provider

  const adopt = useCallback(async (next: AiCredentialStatus) => {
    queryClient.setQueryData(queryKeys.aiCredential(workspace.slug), next)
    await Promise.all([refresh(), queryClient.invalidateQueries({ queryKey: issueKeys.session }), queryClient.invalidateQueries({ queryKey: ['ai-models', workspace.slug] })])
  }, [queryClient, workspace.slug, refresh])

  const authorize = useMutation({
    mutationFn: () => aiCredentialsApi.authorize(workspace.slug, provider),
    onSuccess: response => {
      setAuthorization(response)
      setCode('')
      setDeviceError(null)
      window.open(response.authorize_url, '_blank', 'noopener,noreferrer')
    },
  })
  const connect = useMutation({
    mutationFn: () => aiCredentialsApi.connect(workspace.slug, code.trim()),
    onSuccess: async next => { setAuthorization(null); setCode(''); await adopt(next) },
  })
  const disconnect = useMutation({
    mutationFn: () => aiCredentialsApi.disconnect(workspace.slug),
    onSuccess: async next => { setConfirmOpen(false); setAuthorization(null); await adopt(next) },
  })
  const cancel = useMutation({
    mutationFn: async () => {
      if (authorization?.request_id) await aiCredentialsApi.cancelChatGPT(workspace.slug, authorization.request_id)
    },
    onSuccess: () => { setAuthorization(null); setCode(''); setDeviceError(null); connect.reset() },
  })

  useEffect(() => {
    if (!authorization?.request_id) return
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout>
    const interval = Math.max(5, authorization.interval_seconds ?? 5) * 1000
    const poll = async () => {
      if (Date.now() >= Date.parse(authorization.expires_at)) {
        setDeviceError('That ChatGPT sign-in expired. Cancel and connect again.'); return
      }
      try {
        const result = await aiCredentialsApi.pollChatGPT(workspace.slug, authorization.request_id!, controller.signal)
        if (controller.signal.aborted) return
        if (!result.pending && result.status) {
          setAuthorization(null)
          setDeviceError(null)
          await adopt(result.status)
          return
        }
        setDeviceError(null)
      } catch (error) {
        if (controller.signal.aborted) return
        setDeviceError(messageFor(error))
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return
      }
      if (!controller.signal.aborted) timer = setTimeout(() => void poll(), interval)
    }
    timer = setTimeout(() => void poll(), interval)
    return () => { controller.abort(); clearTimeout(timer) }
  }, [authorization, workspace.slug, adopt])

  function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (code.trim()) connect.mutate()
  }

  const busy = authorize.isPending || connect.isPending || disconnect.isPending || cancel.isPending
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="AI provider" description="Connect Claude or ChatGPT to draft issues with your own account." />
        <div className="space-y-5 p-5">
          {canManage && <div role="group" aria-label="AI provider" className="grid gap-3 sm:grid-cols-2">
            {(['anthropic', 'openai'] as const).map(value => <button key={value} type="button" aria-pressed={provider === value}
              disabled={busy || Boolean(authorization)} onClick={() => { setChoice(value); authorize.reset(); setDeviceError(null) }}
              className={cn('rounded-lg border p-4 text-left transition disabled:opacity-60', provider === value ? 'border-brand-600 bg-brand-50 dark:bg-brand-950' : 'border-ink-200 hover:border-ink-400 dark:border-ink-700')}>
              <span className="block text-sm font-semibold">{providerName(value)}</span>
              <span className={cn('mt-1 block text-xs', mutedTextClass)}>{value === 'openai' ? 'Sign in with your ChatGPT account' : 'Sign in with your Claude account'}</span>
            </button>)}
          </div>}
          {!canManage ? <p className={cn('text-sm', mutedTextClass)}>{connected ? 'AI drafting is connected for this workspace.' : 'AI drafting is not connected yet.'} Only an admin can change the provider and model.</p>
            : status.isPending ? <SkeletonRows rows={2} />
            : status.isError ? <Alert tone="danger">{messageFor(status.error)}</Alert>
            : selectedConnected && status.data ? <ConnectedPanel status={status.data} />
            : <p className={cn('text-sm', mutedTextClass)}>{connected ? `Connecting ${name} will replace the ${providerName(status.data?.provider)} connection after approval.` : `Connect ${name} to start drafting. No API key is needed.`}</p>}

          {canManage && !status.isPending && !status.isError && !authorization && <div className="flex flex-wrap gap-2">
            <Button variant="primary" loading={authorize.isPending} disabled={busy} onClick={() => authorize.mutate()}>{selectedConnected ? `Reconnect ${name}` : `Connect ${name}`}</Button>
            {selectedConnected && <Button variant="danger" disabled={busy} onClick={() => setConfirmOpen(true)}>Disconnect</Button>}
          </div>}
          {authorize.error && <Alert tone="danger">{messageFor(authorize.error)}</Alert>}
          {cancel.error && <Alert tone="danger">{messageFor(cancel.error)}</Alert>}
          {deviceError && <Alert tone="danger">{deviceError}</Alert>}

          {authorization && (authorization.user_code ? <div className="space-y-4 rounded-card border border-brand-200 bg-brand-50/60 p-4 dark:border-brand-900 dark:bg-brand-950/40">
            <p className="text-sm">Open ChatGPT, sign in, and enter this code to authorize your account for Taskly’s AI drafting through Codex.</p>
            <p aria-label="ChatGPT sign-in code" className="select-all font-mono text-2xl font-semibold tracking-widest">{authorization.user_code}</p>
            <p className={cn('text-xs', mutedTextClass)}>You may need to enable device code login in ChatGPT’s security settings. Keep this page open; it will update after approval.</p>
            <div role="status" className="text-sm text-ink-500">Waiting for ChatGPT approval…</div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => window.open(authorization.authorize_url, '_blank', 'noopener,noreferrer')}>Open ChatGPT</Button>
              <Button loading={cancel.isPending} onClick={() => cancel.mutate()}>Cancel sign-in</Button>
            </div>
          </div> : <form onSubmit={submitCode} className="space-y-3 rounded-card border border-brand-200 bg-brand-50/60 p-4 dark:border-brand-900 dark:bg-brand-950/40">
            <p className="text-sm">Approve access in the Claude tab, then paste the code Claude shows you.</p>
            <TextField autoFocus label="Code from Claude" value={code} spellCheck={false} autoCapitalize="off" autoComplete="off" disabled={connect.isPending}
              onChange={event => setCode(event.target.value)} error={connect.error ? messageFor(connect.error) : undefined} />
            <div className="flex flex-wrap gap-2">
              <Button type="submit" variant="primary" loading={connect.isPending} disabled={!code.trim()}>Finish connecting</Button>
              <Button disabled={connect.isPending} onClick={() => cancel.mutate()}>Cancel</Button>
              <Button onClick={() => window.open(authorization.authorize_url, '_blank', 'noopener,noreferrer')}>Open Claude</Button>
            </div>
          </form>)}
        </div>
      </Card>

      {canManage && selectedConnected && status.data && <ModelPicker key={`${status.data.provider}:${status.data.model}`} slug={workspace.slug} provider={provider} onSaved={adopt} />}
      <McpCard />
      <Card>
        <CardHeader title="How AI drafting works" />
        <div className={cn('space-y-2 p-5 text-sm', mutedTextClass)}>
          <p>Anyone who can create issues in this workspace can draft using the connected account and selected model. Requests count against that account’s usage.</p>
          <p>Connections are encrypted and separate for each workspace. You review and edit drafts before saving them as issues.</p>
        </div>
      </Card>
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={() => disconnect.mutate()}
        title={`Disconnect ${providerName(status.data?.provider)}?`} description="This stops AI drafting for everyone in this workspace. Existing issues are kept."
        confirmLabel="Disconnect" loading={disconnect.isPending} error={disconnect.error ? messageFor(disconnect.error) : null} />
    </div>
  )
}

function ModelPicker({ slug, provider, onSaved }: { slug: string; provider: AiProvider; onSaved: (next: AiCredentialStatus) => Promise<void> }) {
  const models = useQuery({ queryKey: ['ai-models', slug, provider], queryFn: ({ signal }) => aiCredentialsApi.models(slug, signal), staleTime: 60_000 })
  const [choice, setChoice] = useState<string | null>(null)
  const selected = choice ?? models.data?.selected_model ?? ''
  const available = models.data?.models.some(model => model.id === selected)
  const save = useMutation({ mutationFn: () => aiCredentialsApi.selectModel(slug, selected), onSuccess: onSaved })
  return <Card>
    <CardHeader title="Drafting model" description={`Choose which ${providerName(provider)} model this workspace uses.`} />
    <div className="space-y-4 p-5">
      {models.isPending ? <SkeletonRows rows={2} /> : models.error ? <Alert tone="danger">{messageFor(models.error)}</Alert> : <SelectField label="Model" value={selected} disabled={save.isPending}
        hint="Available models come from your connected account. Different models can have different speed and usage limits."
        onChange={event => { setChoice(event.target.value); save.reset() }}>
        {!available && <option value={selected} disabled>{selected ? `${selected} — choose an available model` : 'Choose a model'}</option>}
        {models.data?.models.map(model => <option key={model.id} value={model.id}>{model.name || model.id}</option>)}
      </SelectField>}
      {save.error && <Alert tone="danger">{messageFor(save.error)}</Alert>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button onClick={() => void models.refetch()} loading={models.isFetching}>Refresh models</Button>
        <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending} disabled={!available || selected === models.data?.selected_model}>Save model</Button>
      </div>
    </div>
  </Card>
}

/** Provenance for a connected workspace. Never a token — there is no token to show. */
function ConnectedPanel({ status }: { status: AiCredentialStatus }) {
  return (
    <div className="space-y-3">
      <Alert tone="success">Connected. Anyone who can create issues here can draft them with AI.</Alert>
      <dl className="divide-y divide-ink-200 rounded-card border border-ink-200 text-sm dark:divide-ink-800 dark:border-ink-800">
        <Row label="Connected by" value={status.connected_by?.name ?? 'Someone who has since left'} />
        <Row label="Connected" value={status.connected_at ? formatDateTime(status.connected_at) : '—'} />
        <Row
          label="Last used"
          value={status.last_used_at ? formatDateTime(status.last_used_at) : 'Not used yet'}
        />
      </dl>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
      <dt className={cn(mutedTextClass)}>{label}</dt>
      <dd className="truncate font-medium">{value}</dd>
    </div>
  )
}

/**
 * The one-line MCP setup, copyable.
 *
 * The command is the same script the iPad app and the marketing page hand out —
 * it detects the tool, runs the browser login and writes that tool's config —
 * so there is one thing to keep working rather than three. It belongs on this
 * page because this is where a workspace's AI already lives.
 *
 * It is deliberately NOT gated on the Claude connection above. That connection
 * is what drafts issues inside Taskly; MCP is how an agent on someone's own
 * machine reads the board. They are independent, and hiding this until Claude
 * was connected would imply a dependency that does not exist.
 */
function McpCard() {
  const { copied, copy } = useCopyToClipboard()
  const command = 'curl -fsSL https://tasklypro.app/scripts/install | bash'

  return (
    <Card>
      <CardHeader
        title="Connect a coding agent"
        description="Give Claude Code, Codex, Cursor, Windsurf, OpenCode or Antigravity access to this workspace's board over MCP."
      />
      <div className="space-y-4 p-5">
        <div className="overflow-hidden rounded-lg border border-ink-200 bg-ink-950 dark:border-ink-700">
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-1.5">
            <Terminal className="size-3.5 text-ink-400" aria-hidden="true" />
            <span className="text-[11px] font-medium tracking-wide text-ink-400">Terminal</span>
            <button
              type="button"
              onClick={() => void copy(command, 'mcp')}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-ink-300 transition hover:bg-white/10 hover:text-white"
            >
              {copied === 'mcp' ? (
                <>
                  <Check className="size-3.5 text-green-400" aria-hidden="true" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="size-3.5" aria-hidden="true" />
                  Copy
                </>
              )}
            </button>
          </div>
          {/* A long command must scroll in its own box rather than widening the
              settings column. */}
          <div className="overflow-x-auto px-3 py-3">
            <code className="font-mono text-xs whitespace-pre text-ink-100">
              <span className="select-none text-ink-500">$ </span>
              {command}
            </code>
          </div>
        </div>
        <p className={cn('text-sm', mutedTextClass)}>
          The script detects your tool, signs you in through the browser and writes that tool&rsquo;s
          config. Your agent then reads the backlog, picks up the next task and marks work done
          &mdash; against this workspace, with your own account, never anybody else&rsquo;s board.
        </p>
      </div>
    </Card>
  )
}
